/*
  CostCentresComponent.js - Cost Centres
*/

// NPM
import { Box, Button, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';

// COMPONENTS
import AddCostCenter from '../../components/account/costcenters/AddCostCenter';

import EditCostCenter from '../../components/account/costcenters/EditCostCenter';

// I18N
import formattedMessages from '../../messages/account/CostCenterFormattedMessages';

// NETWORK
import CostCentersTable from '../../components/account/costcenters/CostCentersTable';
import Layout from '../../components/layouts/Layout';
import useCostCentreRequest from '../../requests/CostCentreRequest';
import { AccountRoutes } from '../../routes/AccountRoutes';
import EditAdministrator from './EditAdministrator';

const CostCenters = ({ customer, errorHandler, accessLevel, accessType }) => {
  const { getCentres } = useCostCentreRequest();
  const [state, setState] = useState({
    selected: null,
    centres: [],
    addNew: false,
    contactId: null,
    loaded: false,
    isLoading: true,
  });

  useEffect(() => {
    const loadCentres = async () => {
      if (customer) {
        try {
          setState((prev) => ({ ...prev, isLoading: true }));
          const centres = await getCentres(errorHandler);
          setState((prev) => ({
            ...prev,
            centres,
            loaded: true,
            isLoading: false,
          }));
        } catch (error) {
          console.error('Error loading centres:', error);
          errorHandler?.(error);
          setState((prev) => ({
            ...prev,
            loaded: true,
            isLoading: false,
          }));
        }
      }
    };

    loadCentres();
  }, [customer, getCentres, errorHandler]);

  const handleAddNew = () => {
    setState((prev) => ({ ...prev, addNew: true }));
  };

  const handleCostCentreClick = (centre) => {
    setState((prev) => ({ ...prev, selected: centre }));
  };

  const handleContactClick = (centre) => {
    setState((prev) => ({ ...prev, contactId: centre.customer_contact_id }));
  };

  const handleBackClick = async (refreshFlag) => {
    let newState = {
      selected: null,
      addNew: false,
      contactId: null,
    };

    if (refreshFlag) {
      try {
        setState((prev) => ({ ...prev, isLoading: true }));
        const centres = await getCentres(errorHandler);
        newState.centres = centres;
      } catch (error) {
        console.error('Error refreshing centres:', error);
        errorHandler?.(error);
      } finally {
        newState.isLoading = false;
      }
    }

    setState((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const renderContent = () => {
    const { addNew, selected, contactId, centres, isLoading } = state;

    if (contactId) {
      return (
        <EditAdministrator
          contactId={contactId}
          handleBackClick={handleBackClick}
          errorHandler={errorHandler}
          noTab={true}
        />
      );
    }

    if (addNew) {
      return (
        <AddCostCenter
          handleBackClick={handleBackClick}
          errorHandler={errorHandler}
        />
      );
    }

    if (selected) {
      return (
        <EditCostCenter
          handleBackClick={handleBackClick}
          centre={selected}
          errorHandler={errorHandler}
        />
      );
    }

    return (
      <>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAddNew}
            sx={{ display: 'inline-flex' }}
          >
            {formattedMessages.addNew}
          </Button>
        </Stack>

        <Box>
          <CostCentersTable
            list={centres}
            handleCostCentreClick={handleCostCentreClick}
            handleContactClick={handleContactClick}
            isLoading={isLoading}
          />
        </Box>
      </>
    );
  };

  const handleBackButton = () => {
    setState((prev) => ({ ...prev, selected: null, addNew: false }));
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <Layout
      routes={AccountRoutes}
      backButtonText={
        state.selected || state.addNew ? 'Back to Cost Centers' : null
      }
      handleBackButton={handleBackButton}
      title={
        state.addNew
          ? formattedMessages.addNewCostCenter
          : state.selected
          ? formattedMessages.editCostCenter
          : formattedMessages.costCenters
      }
      accessLevel={accessLevel}
      accessType={accessType}
      isHidden={!customer.hasCampaign}
    >
      {renderContent()}
    </Layout>
  );
};

export default CostCenters;
